// recents.ts — the "jump back in" cards above the release list.
//
// Backed by GET /api/recents (release_visits, migration 033; the pin column
// is migration 057), which is per-user and ordered pinned-first, then by when
// THIS user last opened each release — deliberately a different signal from
// the grid's ordering below it. The grid ranks by activity of any kind,
// including work other people did; these cards answer "what was I in" (and,
// since the jump-back-in seam, "what did I nail to the shelf"). Two lanes on
// one page only earns its keep if they can disagree, and they routinely do.
//
// Aborted releases are filtered server-side, so a card never invites you back
// into a release that has been shut down.
//
// WHAT A CARD SHOWS (Bridge-UI seam jump-back-in): the status pill, the
// release NUMBER (R49), the pinned badge when pinned (R41), the name, the
// live FLEET + THROUGHPUT cell where the owner name used to be (R44 — the
// owner said "instead of"), the TOTAL COST (R45), and when this user last
// opened it. The live cell and the cost come through the `live` resolver the
// caller passes — home.ts resolves them from the SAME fleet-metrics sweep and
// cost map the grid columns read, so a card and its row can never disagree.

import { icon } from './generated/icons.js';
import { formatRelativeLong } from './relative-time.js';

export interface RecentRelease {
  id: string;
  name: string;
  slug: string;
  status: 'ideating' | 'active' | 'completed' | 'aborted';
  /** The corpus register id (REL-NNN); null until the cutter mints it. */
  rel_id?: string | null;
  /** R41 — pinned to the shelf (per-user, release_visits.pinned). */
  pinned?: boolean;
  owner_name: string | null;
  owner_email: string | null;
  /** OIDC profile picture (item 55.9) — null renders initials. */
  owner_picture_url?: string | null;
  last_visited_at: string | null;
  last_activity_at: string | null;
  last_vm_activity_at: string | null;
}

/** What the caller resolves for one card's LIVE half (R44/R45): the fleet/
 * throughput cell markup and the cost text, both derived from the grid's own
 * stores. `html: null` renders the honest em dash. The `fresh` flags mark a
 * value that JUST materialized ('—' -> data), which is the one moment the
 * field-transition animation fires — not on every 30 s repaint. */
export interface CardLive {
  html: string | null;
  tip: string;
  costText: string;
  costTip: string;
  liveFresh?: boolean;
  costFresh?: boolean;
  /** REL-096: the escalation scorecard line ("2 decisions waiting" / "5
   * open"); null/absent renders nothing — an unmeasured Bridge is silent,
   * never "0 open". `escLoud` = a human is being asked. */
  escText?: string | null;
  escTip?: string;
  escLoud?: boolean;
  /** Run position (aisdlc-docs § 30) — the Bridge's own line for this
   * release, folded by fleet-metrics: the step chip ("S10 implement"), the
   * state word and its tone, the blocker/lever text. null/absent renders
   * nothing (an unreachable or older Bridge is silent, never "S01"). */
  rpStep?: string | null;
  rpState?: string | null;
  rpTone?: 'live' | 'dead' | 'stale' | 'done' | 'neutral';
  rpText?: string | null;
  rpTip?: string;
}
export type CardLiveResolver = (releaseId: string) => CardLive | null;

export interface RenderRecentsOpts {
  live?: CardLiveResolver;
  /** True on every render AFTER the first: suppresses the one-shot entrance
   * stagger (an innerHTML rebuild recreates every element, and the entrance
   * animation would otherwise re-fire atop every reorder/repaint). */
  reenter?: boolean;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}

/** The href a card opens. Null on a host with no subdomain scheme (localhost,
 * a bare IP), where the card renders as a non-link rather than a dead `#`. */
export function cardHref(row: RecentRelease, apex: string | null): string | null {
  return apex ? `https://${row.slug}.${apex}/` : null;
}

/**
 * One card's markup.
 *
 * The time shown is LAST VISITED — the recency fact the unpinned half is
 * sorted by, so the ordering is legible instead of mysterious. `data-rel-time`
 * hands it to the shared ticker, which is what makes "5 min ago" keep
 * counting without this module owning a timer.
 */
export function cardHtml(row: RecentRelease, apex: string | null, live?: CardLive | null): string {
  const href = cardHref(row, apex);
  const tag = href ? 'a' : 'div';
  // Item 29 (owner): a recents card opens the release's Helm in a NEW tab,
  // exactly like the grid's name link below it — the home screen stays put.
  const attrs = href ? ` href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer"` : '';
  const liveHtml = live?.html ?? null;
  // #927: `live` is absent only before the card's live resolver has run at
  // all. That is a real state and it now says so, rather than borrowing the
  // em dash that used to mean four different things in this column.
  const costText = live?.costText ?? 'Unknown';
  // data-rel: the release id the card names — the right-click context menu
  // (item 55.1) resolves the grid row through it, so the card and the row
  // share ONE menu implementation.
  return `<${tag} class="hc-card${row.pinned ? ' hc-card-pinned' : ''}" data-rel="${escapeHtml(row.id)}"${attrs}>
    <div class="hc-card-top">
      <span class="hc-card-idents">
        <span class="hc-pill hc-pill-${row.status}">${escapeHtml(row.status)}</span>
        ${row.rel_id ? `<span class="hc-card-relid" data-tip="Release number">${escapeHtml(row.rel_id)}</span>` : ''}
      </span>
      <span class="hc-card-tools">
        ${row.pinned ? `<span class="hc-card-pin" data-tip="Pinned — stays in Jump Back In (right-click to unpin)">${icon('pin')}</span>` : ''}
        ${href ? `<span class="hc-card-go">${icon('arrow-up-right')}</span>` : ''}
      </span>
    </div>
    <div class="hc-card-name">${escapeHtml(row.name)}</div>
    <div class="hc-card-live${live?.liveFresh ? ' hc-cell-fresh' : ''}"${live?.tip ? ` data-tip="${escapeHtml(live.tip)}"` : ''}>${
      liveHtml ?? '<span class="hc-card-nolive">—</span>'
    }</div>
    ${live?.escText
      ? `<div class="hc-card-esc"><span class="hc-badge-esc${live.escLoud ? ' hc-badge-esc-loud' : ''}"${
          live.escTip ? ` data-tip="${escapeHtml(live.escTip)}"` : ''}>${escapeHtml(live.escText)}</span></div>`
      : ''}
    ${live?.rpStep
      ? `<div class="hc-card-rp hc-rp-tone-${live.rpTone ?? 'neutral'}"${live.rpTip ? ` data-tip="${escapeHtml(live.rpTip)}"` : ''}>`
        + `<span class="hc-rp-step">${escapeHtml(live.rpStep)}</span>`
        + (live.rpState ? `<span class="hc-rp-state">${escapeHtml(live.rpState)}</span>` : '')
        + (live.rpText ? `<span class="hc-rp-text">${escapeHtml(live.rpText)}</span>` : '')
        + '</div>'
      : ''}
    <div class="hc-card-meta">
      <span class="hc-card-cost${live?.costFresh ? ' hc-cell-fresh' : ''}" data-tip="${escapeHtml(live?.costTip ?? 'Total cost could not be determined.\nreason:  this card\u2019s live data has not been resolved yet')}">${escapeHtml(costText)}</span>
      <span class="hc-card-when" data-rel-time="${escapeHtml(row.last_visited_at ?? '')}"
            data-tip="last opened by you">${escapeHtml(formatRelativeLong(row.last_visited_at))}</span>
    </div>
  </${tag}>`;
}

/**
 * Render the strip into `host`.
 *
 * An EMPTY list hides the whole section rather than showing an empty shelf:
 * a user who has not opened anything yet is the normal first-run state, and a
 * headed, empty container reads as a feature that is broken rather than one
 * that has nothing to say. `hidden` is set on the host so the grid moves up.
 *
 * RE-RENDERS (opts.reenter): the strip is re-painted in place when the cost
 * map and the fleet sweep land after the grid is up, on every fleet tick, and
 * on pin/unpin — renderRecents is NOT called once any more. Those repaints
 * suppress the entrance stagger via the host class; reorder motion is the
 * caller's withFlip around this call.
 */
export function renderRecents(
  host: HTMLElement,
  rows: RecentRelease[],
  apex: string | null,
  opts?: RenderRecentsOpts,
): void {
  if (!rows.length) {
    host.hidden = true;
    host.innerHTML = '';
    return;
  }
  host.hidden = false;
  host.classList.toggle('hc-no-entrance', !!opts?.reenter);
  host.innerHTML = `
    <div class="hc-section-head">
      ${icon('history')}<span>Jump back in</span>
    </div>
    <div class="hc-cards">${rows.map((r) => cardHtml(r, apex, opts?.live?.(r.id) ?? null)).join('')}</div>`;
  if (!opts?.reenter) {
    // Stagger the entrance so the strip reads as one gesture rather than four
    // simultaneous pops. Capped: with a long list the last card would
    // otherwise arrive noticeably after the user has already started reading.
    // FIRST render only — on re-renders the recreated elements would replay
    // the whole entrance atop every repaint (the hc-no-entrance class above
    // is the belt to this suspender).
    for (const [i, el] of [...host.querySelectorAll<HTMLElement>('.hc-card')].entries()) {
      el.style.setProperty('--hc-stagger', `${Math.min(i, 6) * 40}ms`);
    }
  }
}

/** Fetch the cards. Failure-tolerant: recents are a convenience, so any
 * non-200 yields an empty strip that simply does not render — never an error
 * banner over a working releases list. The server may return MORE than
 * `limit` rows when the user has more pins than slots (every pin stays,
 * R41); this passes them through untrimmed. */
export async function fetchRecents(
  fetcher: (url: string) => Promise<{ status: number; data: unknown }>,
  limit = 4,
): Promise<RecentRelease[]> {
  const { status, data } = await fetcher(`/api/recents?limit=${limit}`);
  if (status !== 200 || !data || typeof data !== 'object') return [];
  const rows = (data as { recents?: unknown }).recents;
  return Array.isArray(rows) ? (rows as RecentRelease[]) : [];
}
